import styled from '@emotion/styled';
import { Button, DatePicker, Drawer, Input, Modal, Select, Tabs } from 'antd';
import { orderBy } from 'lodash';
import moment, { Moment } from 'moment';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { FaChartLine, FaFilter, FaMoneyBillWave, FaReceipt } from 'react-icons/fa';
import { FiRefreshCcw, FiSearch } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { PRIMARY, YELLOW } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';
import { ExportFormatButton } from '../../../../shared/ExportFormatButton';
import { getExportMetadata } from '../../../../shared/exportFiles';
import { Layout } from '../../../shared/Layout';
import { StatusPill } from '../../shared/StatusPill';
import { OrdreAchat } from '../components/reporting/OrdreAchat';
import { OrdreVente } from '../components/reporting/OrdreVente';
import { TransactionEnCours } from '../components/reporting/TransactionE';
import { TransactionFinalise } from '../components/reporting/TransactionF';
import { exportOperationReportingData } from '../components/exportDataExcel';
import { getOperationReporting } from '../network';

const Container = styled.div`
  min-height: calc(100vh - 76px);
  background:
    radial-gradient(circle at 8% 0%, rgba(255, 79, 31, 0.08), transparent 24%),
    radial-gradient(circle at 90% 8%, rgba(255, 176, 0, 0.08), transparent 20%),
    var(--mt-bg);
  color: var(--mt-text);
  font-family: Poppins-R;

  .bread-crum span {
    color: var(--mt-text);
  }

  td.ant-table-column-sort {
    background-color: transparent !important;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: #fff7e8 !important;
    font-weight: bold;
  }

  .odd-row {
    background-color: var(--mt-row-odd);
    color: var(--mt-text);
  }

  .even-row {
    background-color: var(--mt-row-even);
    color: var(--mt-text);
  }

  .odd-row,
  .even-row {
    &:hover > td {
      background-color: var(--mt-row-hover) !important;
      cursor: pointer;
      color: var(--mt-text);
    }
  }

  .content {
    padding: 12px;
    width: 100%;
    color: var(--mt-text);
  }

  .transaction-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 18px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.13), transparent 38%),
      ${PRIMARY};
    box-shadow: var(--mt-card-shadow);
  }

  .eyebrow {
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .transaction-hero h1 {
    margin: 5px 0 0;
    color: #fff7e8;
    font-family: Poppins-B;
    font-size: 30px;
    line-height: 1.1;
  }

  .transaction-hero p {
    max-width: 820px;
    margin: 8px 0 0;
    color: rgba(255, 247, 232, 0.72);
    line-height: 1.5;
  }

  .hero-actions,
  .page-toolbar {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .ghost-action,
  .primary-action {
    border-radius: 999px;
    font-family: Poppins-B;
  }

  .ghost-action {
    border-color: rgba(255, 176, 0, 0.28);
    background: rgba(255, 255, 255, 0.06);
    color: #fff7e8;
  }

  .primary-action {
    border: 0;
    background: linear-gradient(135deg, #ff4f1f, ${YELLOW});
    color: #07141c;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
  }

  .kpi-card,
  .toolbar,
  .report-shell {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    background: var(--mt-card);
    box-shadow: var(--mt-card-shadow);
  }

  .kpi-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 78px;
    padding: 13px;
  }

  .kpi-card span {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .kpi-card strong {
    display: block;
    margin-top: 6px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 21px;
  }

  .kpi-card .icon {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: 1px solid rgba(255, 176, 0, 0.24);
    border-radius: 8px;
    color: var(--mt-warning);
    background: var(--mt-surface);
  }

  .toolbar {
    display: grid;
    grid-template-columns: minmax(300px, 1fr) auto;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
    padding: 12px;
  }

  .search-box {
    position: relative;
  }

  .search-box svg {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 12px;
    color: var(--mt-muted);
    transform: translateY(-50%);
  }

  .search-box .ant-input {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    padding-left: 36px;
    background: var(--mt-surface);
    color: var(--mt-text);
  }

  .table-count {
    border: 1px solid rgba(255, 176, 0, 0.25);
    border-radius: 999px;
    padding: 5px 9px;
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 11px;
    white-space: nowrap;
  }

  .reset-filter {
    height: 38px;
    border-color: var(--mt-border);
    border-radius: 999px;
    background: var(--mt-surface);
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .filter-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(130px, 1fr));
    gap: 8px;
    margin-top: 10px;
  }

  .filter-grid .ant-select-selector,
  .filter-grid .ant-picker {
    min-height: 38px;
    border-color: var(--mt-border) !important;
    border-radius: 8px !important;
    background: var(--mt-surface) !important;
    color: var(--mt-text) !important;
  }

  .filter-grid .ant-select-selection-item,
  .filter-grid .ant-picker-input input {
    color: var(--mt-text) !important;
  }

  .filter-grid .ant-select-selection-placeholder,
  .filter-grid .ant-picker-input input::placeholder {
    color: var(--mt-muted) !important;
  }

  .tab-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-top: 12px;
    margin-bottom: 12px;
  }

  .summary-card {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px 12px;
    background: var(--mt-surface);
  }

  .summary-card span {
    display: flex;
    align-items: center;
    gap: 7px;
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .summary-card strong {
    display: block;
    margin-top: 6px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 18px;
  }

  .report-shell {
    margin-top: 12px;
    overflow: hidden;
  }

  .desk-strip {
    display: grid;
    grid-template-columns: 1.2fr repeat(3, minmax(0, 1fr));
    gap: 10px;
    padding: 14px 14px 0;
  }

  .desk-card {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 11px 12px;
    background: var(--mt-surface);
  }

  .desk-card span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .desk-card strong {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 17px;
  }

  .desk-card.primary {
    background:
      linear-gradient(135deg, rgba(255, 176, 0, 0.14), transparent 45%),
      ${PRIMARY};
  }

  .desk-card.primary span {
    color: rgba(255, 247, 232, 0.7);
  }

  .desk-card.primary strong {
    color: #fff7e8;
  }

  .report-shell .ant-tabs-nav {
    margin: 0;
    padding: 0 14px;
    background: ${PRIMARY};
  }

  .report-shell .ant-tabs-tab {
    color: rgba(255, 247, 232, 0.74);
    font-family: Poppins-B;
  }

  .report-shell .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${YELLOW} !important;
  }

  .report-shell .ant-tabs-ink-bar {
    background: ${YELLOW};
  }

  .report-shell .ant-tabs-content-holder {
    padding: 14px;
  }

  .reporting-table .ant-table {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    overflow: hidden;
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .reporting-table .ant-table-thead > tr > th {
    border-bottom: 1px solid var(--mt-border);
    font-size: 11px;
    text-transform: uppercase;
  }

  .reporting-table .ant-table-tbody > tr > td {
    border-bottom: 1px solid var(--mt-border);
    color: var(--mt-text);
  }

  .party-cell,
  .security-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .party-cell strong,
  .security-cell strong {
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .party-cell span,
  .security-cell span {
    color: var(--mt-muted);
    font-size: 11px;
  }

  .amount-cell {
    display: block;
    text-align: right;
    font-family: Poppins-B;
    white-space: nowrap;
  }

  .operation-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 62px;
    border-radius: 999px;
    padding: 4px 8px;
    font-family: Poppins-B;
    font-size: 10px;
    text-transform: uppercase;
  }

  .operation-chip.buy {
    border: 1px solid rgba(0, 150, 93, 0.24);
    background: rgba(0, 150, 93, 0.1);
    color: #00965d;
  }

  .operation-chip.sell {
    border: 1px solid rgba(220, 38, 38, 0.24);
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;
  }

  .buy-text,
  .success-text {
    color: #00965d;
  }

  .sell-text,
  .danger-text {
    color: #dc2626;
  }

  .detail-drawer {
    background: var(--mt-card);
    color: var(--mt-text);
  }

  .detail-hero {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 14px;
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.1), transparent 42%),
      var(--mt-surface);
  }

  .detail-hero span {
    color: var(--mt-muted);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .detail-hero strong {
    display: block;
    margin-top: 5px;
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 22px;
  }

  .detail-total {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-top: 10px;
  }

  .detail-total div {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 9px;
    background: var(--mt-card);
  }

  .detail-total span {
    color: var(--mt-muted);
    font-size: 10px;
  }

  .detail-total strong {
    margin-top: 3px;
    font-size: 14px;
  }

  .detail-section {
    margin-top: 12px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    overflow: hidden;
    background: var(--mt-card);
  }

  .detail-section h3 {
    margin: 0;
    padding: 10px 12px;
    border-bottom: 1px solid var(--mt-border);
    background: ${PRIMARY};
    color: ${YELLOW};
    font-family: Poppins-B;
    font-size: 13px;
    text-transform: uppercase;
  }

  .timeline {
    display: grid;
    gap: 8px;
    padding: 12px;
  }

  .timeline-step {
    display: grid;
    grid-template-columns: 22px 1fr auto;
    align-items: center;
    gap: 9px;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 9px;
    background: var(--mt-surface);
  }

  .timeline-dot {
    width: 11px;
    height: 11px;
    border-radius: 999px;
    background: var(--mt-border);
    box-shadow: 0 0 0 4px rgba(255, 176, 0, 0.05);
  }

  .timeline-step.done .timeline-dot {
    background: #00965d;
  }

  .timeline-step.current .timeline-dot {
    background: ${YELLOW};
  }

  .timeline-step strong {
    display: block;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .timeline-step span {
    display: block;
    color: var(--mt-muted);
    font-size: 11px;
  }

  .timeline-status {
    border: 1px solid rgba(255, 176, 0, 0.25);
    border-radius: 999px;
    padding: 4px 8px;
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 10px;
    white-space: nowrap;
  }

  .matching-desk {
    display: grid;
    grid-template-columns: 1fr 40px 1fr;
    gap: 8px;
    padding: 12px;
  }

  .leg-card {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 10px;
    background: var(--mt-surface);
  }

  .leg-card span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  .leg-card strong {
    display: block;
    margin-top: 4px;
    color: var(--mt-text);
    font-family: Poppins-B;
  }

  .match-arrow {
    display: grid;
    place-items: center;
    color: ${YELLOW};
    font-family: Poppins-B;
  }

  .control-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    padding: 12px;
  }

  .control-actions button {
    border-radius: 999px;
    font-family: Poppins-B;
  }

  .control-actions .validate-action {
    border: 0;
    background: #00965d;
    color: white;
  }

  .control-actions .reject-action {
    border-color: rgba(220, 38, 38, 0.3);
    color: #dc2626;
  }

  .control-actions .settle-action {
    border-color: rgba(255, 176, 0, 0.28);
    background: var(--mt-surface);
    color: var(--mt-warning);
  }

  .action-modal .ant-modal-content {
    border: 1px solid var(--mt-border);
    border-radius: 10px;
    overflow: hidden;
    background: var(--mt-card);
  }

  .action-modal .ant-modal-header {
    background: ${PRIMARY};
  }

  .action-modal .ant-modal-title {
    color: ${YELLOW};
    font-family: Poppins-B;
  }

  .action-review {
    color: var(--mt-text);
  }

  .action-review p {
    margin: 0 0 12px;
    color: var(--mt-muted);
  }

  .action-review .review-box {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 12px;
    background: var(--mt-surface);
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    padding: 12px;
  }

  .detail-row {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 9px;
    background: var(--mt-surface);
  }

  .detail-row span {
    display: block;
    color: var(--mt-muted);
    font-size: 10px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .detail-row strong {
    display: block;
    margin-top: 4px;
    color: var(--mt-text);
    font-family: Poppins-B;
    overflow-wrap: anywhere;
  }

  .detail-row.full {
    grid-column: 1 / -1;
  }

  @media (max-width: 760px) {
    .content {
      padding: 18px 12px;
    }

    .transaction-hero,
    .toolbar {
      align-items: stretch;
      display: flex;
      flex-direction: column;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }

    .filter-grid,
    .desk-strip,
    .matching-desk,
    .tab-summary {
      grid-template-columns: 1fr;
    }
  }
`;

const KpiCard = ({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string | number;
}) => (
  <div className='kpi-card'>
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
    <div className='icon'>{icon}</div>
  </div>
);

const DetailRow = ({
  full,
  label,
  value,
}: {
  full?: boolean;
  label: string;
  value: ReactNode;
}) => (
  <div className={`detail-row ${full ? 'full' : ''}`}>
    <span>{label}</span>
    <strong>{value || '--'}</strong>
  </div>
);

export const ReportingPage = () => {
  // const [isLoading, setIsLoading] = useState(true);
  const [tabIndex, setTabIndex] = useState('1');
  const [reportingData, setReportingData] = useState<any>();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState<any>();
  const [selectedKind, setSelectedKind] = useState('');
  const [actionReview, setActionReview] = useState<'validate' | 'reject' | 'settle' | null>(null);
  const [operationFilter, setOperationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [marketFilter, setMarketFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [dateRange, setDateRange] = useState<[Moment, Moment] | null>(null);
  const connectedUser = useSelector((state: any) => state.userReducer).user;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    await getOperationReporting(connectedUser.id).then(({ data }) => {
      setReportingData(data);
    });
    return;
  };

  const filteredData = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    const filterCollection = (collection: any[] = []) => {
      return collection.filter((row: any) => {
        const searchable = [
          row.code,
          row.typeOperation,
          row.statut,
          row.titre?.pays,
          row.titre?.code,
          row.titre?.instrument,
          row.detenteur?.code_referent,
          row.detenteur?.nom,
          row.detenteur?.prenom,
          row.assetManager?.code_referent,
          row.assetManager?.nom,
          row.partenaire?.code_referent,
          row.partenaire?.nom,
          row.agence?.code,
          row.agence?.nom,
          row.utilisateur?.nom,
          row.utilisateur?.prenom,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        const operation = safeText(row.typeOperation).toUpperCase();
        const rowDate = moment(row.dateOperation || row.created_at);
        const matchesSearch = !term || searchable.includes(term);
        const matchesOperation =
          operationFilter === 'all' ||
          operation === operationFilter.toUpperCase();
        const matchesStatus =
          statusFilter === 'all' || safeText(row.statut) === statusFilter;
        const matchesMarket =
          marketFilter === 'all' || safeText(row.titre?.pays) === marketFilter;
        const holderLabel = `${row.detenteur?.code_referent || ''} ${row.detenteur?.nom || ''} ${row.detenteur?.prenom || ''}`.trim();
        const matchesClient =
          clientFilter === 'all' || holderLabel === clientFilter;
        const matchesDate =
          !dateRange ||
          (rowDate.isValid() &&
            rowDate.isSameOrAfter(dateRange[0], 'day') &&
            rowDate.isSameOrBefore(dateRange[1], 'day'));

        return (
          matchesSearch &&
          matchesOperation &&
          matchesStatus &&
          matchesMarket &&
          matchesClient &&
          matchesDate
        );
      });
    };

    return {
      buyOrders: filterCollection(reportingData?.ope_ACHAT || []),
      sellOrders: filterCollection(reportingData?.ope_VENTE || []),
      matched: filterCollection(orderBy(reportingData?.transact_MATCH, 'created_at', 'desc') || []),
      finalized: filterCollection(orderBy(reportingData?.transact_FIN, 'created_at', 'desc') || []),
    };
  }, [clientFilter, dateRange, marketFilter, operationFilter, reportingData, searchTerm, statusFilter]);

  const filterOptions = useMemo(() => {
    const allRows = [
      ...(reportingData?.ope_ACHAT || []),
      ...(reportingData?.ope_VENTE || []),
      ...(reportingData?.transact_MATCH || []),
      ...(reportingData?.transact_FIN || []),
    ];
    const markets = uniqueOptions(allRows.map((row: any) => row.titre?.pays));
    const clients = uniqueOptions(
      allRows.map((row: any) =>
        `${row.detenteur?.code_referent || ''} ${row.detenteur?.nom || ''} ${row.detenteur?.prenom || ''}`.trim(),
      ),
    );
    const statuses = uniqueOptions(allRows.map((row: any) => row.statut));

    return { clients, markets, statuses };
  }, [reportingData]);

  const stats = useMemo(() => {
    const orderAmount = [...filteredData.buyOrders, ...filteredData.sellOrders].reduce(
      (sum, row: any) => sum + Number(row.qteRestant || 0) * Number(row.prixNominale || 0),
      0,
    );
    const transactionAmount = [...filteredData.matched, ...filteredData.finalized].reduce(
      (sum, row: any) => sum + Number(row.qte || 0) * Number(row.prixNominale || 0),
      0,
    );

    return {
      orders: filteredData.buyOrders.length + filteredData.sellOrders.length,
      transactions: filteredData.matched.length + filteredData.finalized.length,
      matched: filteredData.matched.length,
      amount: orderAmount + transactionAmount,
    };
  }, [filteredData]);

  const currentRows = useMemo(() => {
    if (tabIndex === '1') return filteredData.buyOrders;
    if (tabIndex === '2') return filteredData.sellOrders;
    if (tabIndex === '3') return filteredData.matched;
    return filteredData.finalized;
  }, [filteredData, tabIndex]);

  const tabStats = useMemo(() => {
    const quantity = currentRows.reduce(
      (sum: number, row: any) => sum + getRecordQuantity(row),
      0,
    );
    const amount = currentRows.reduce(
      (sum: number, row: any) => sum + getRecordGrossAmount(row),
      0,
    );
    const commission = currentRows.reduce(
      (sum: number, row: any) => sum + getRecordCommission(row),
      0,
    );

    return {
      amount,
      commission,
      count: currentRows.length,
      quantity,
    };
  }, [currentRows]);

  const deskLabel = useMemo(() => {
    if (tabIndex === '1') return "Ordres d'achat ouverts";
    if (tabIndex === '2') return 'Ordres de vente ouverts';
    if (tabIndex === '3') return 'Transactions matchées';
    return 'Transactions finalisées';
  }, [tabIndex]);

  const resetFilters = () => {
    setSearchTerm('');
    setOperationFilter('all');
    setStatusFilter('all');
    setMarketFilter('all');
    setClientFilter('all');
    setDateRange(null);
  };

  const tabItems = [
    {
      key: '1',
      label: "Ordres d'achat",
      children: (
        <OrdreAchat
          operations={filteredData.buyOrders}
          onSelect={(record) => {
            setSelectedKind("Ordre d'achat");
            setSelectedRecord(record);
          }}
        />
      ),
    },
    {
      key: '2',
      label: 'Ordres de vente',
      children: (
        <OrdreVente
          operations={filteredData.sellOrders}
          onSelect={(record) => {
            setSelectedKind('Ordre de vente');
            setSelectedRecord(record);
          }}
        />
      ),
    },
    {
      key: '3',
      label: 'Transactions matchées',
      children: (
        <TransactionEnCours
          transactions={filteredData.matched}
          onSelect={(record) => {
            setSelectedKind('Transaction matchée');
            setSelectedRecord(record);
          }}
        />
      ),
    },
    {
      key: '4',
      label: 'Transactions finalisées',
      children: (
        <TransactionFinalise
          transactions={filteredData.finalized}
          onSelect={(record) => {
            setSelectedKind('Transaction finalisée');
            setSelectedRecord(record);
          }}
        />
      ),
    },
  ];

  return (
    <Layout>
      <Container>
        {/** Obtain 'current' values in Sidebar component */}
        <div className='content'>
          <section className='transaction-hero'>
            <div>
              <span className='eyebrow'>Gestion des transactions</span>
              <h1>Reporting des opérations</h1>
              <p>
                Pilotez les ordres d'achat, les ordres de vente, les transactions
                matchées et les transactions finalisées depuis un registre unique.
              </p>
            </div>
            <div className='hero-actions'>
              <Button className='ghost-action' icon={<FiRefreshCcw />} onClick={fetchData}>
                Rafraîchir
              </Button>
              <ExportFormatButton
                className='primary-action'
                onExport={(format) => exportOperationReportingData(filteredData, format, getExportMetadata(connectedUser))}
              />
            </div>
          </section>

          <section className='kpi-grid'>
            <KpiCard label='Ordres ouverts' value={stats.orders || '--'} icon={<FaChartLine />} />
            <KpiCard label='Transactions' value={stats.transactions || '--'} icon={<FaChartLine />} />
            <KpiCard label='Matchées' value={stats.matched || '--'} icon={<FaChartLine />} />
            <KpiCard
              label='Exposition'
              value={`${formatNum(stats.amount) || '--'} XAF`}
              icon={<FaChartLine />}
            />
          </section>

          <section className='toolbar'>
            <div>
              <div className='search-box'>
                <FiSearch />
                <Input
                  placeholder='Rechercher titre, détenteur, ordre, agence...'
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              <div className='filter-grid'>
                <Select value={operationFilter} onChange={setOperationFilter}>
                  <Select.Option value='all'>Toutes opérations</Select.Option>
                  <Select.Option value='ACHAT'>Achat</Select.Option>
                  <Select.Option value='VENTE'>Vente</Select.Option>
                </Select>
                <Select value={statusFilter} onChange={setStatusFilter}>
                  <Select.Option value='all'>Tous statuts</Select.Option>
                  {filterOptions.statuses.map((status) => (
                    <Select.Option key={status} value={status}>
                      {formatStatus(status)}
                    </Select.Option>
                  ))}
                </Select>
                <Select value={marketFilter} onChange={setMarketFilter}>
                  <Select.Option value='all'>Tous marchés</Select.Option>
                  {filterOptions.markets.map((market) => (
                    <Select.Option key={market} value={market}>
                      {market}
                    </Select.Option>
                  ))}
                </Select>
                <Select value={clientFilter} onChange={setClientFilter}>
                  <Select.Option value='all'>Tous clients</Select.Option>
                  {filterOptions.clients.map((client) => (
                    <Select.Option key={client} value={client}>
                      {client}
                    </Select.Option>
                  ))}
                </Select>
                <DatePicker.RangePicker
                  value={dateRange as any}
                  onChange={(value) => setDateRange(value as [Moment, Moment] | null)}
                  format='DD/MM/YYYY'
                />
              </div>
            </div>
            <div className='page-toolbar'>
              <span className='table-count'>
                {stats.orders + stats.transactions} lignes affichées
              </span>
              <Button className='reset-filter' onClick={resetFilters}>
                Réinitialiser
              </Button>
            </div>
          </section>

          <section className='report-shell'>
            <div className='desk-strip'>
              <div className='desk-card primary'>
                <span>Vue active</span>
                <strong>{deskLabel}</strong>
              </div>
              <div className='desk-card'>
                <span>Volume traité</span>
                <strong>{formatNum(tabStats.quantity) || '--'}</strong>
              </div>
              <div className='desk-card'>
                <span>Ticket moyen</span>
                <strong>
                  {tabStats.count
                    ? `${formatNum(Math.ceil(tabStats.amount / tabStats.count))} XAF`
                    : '--'}
                </strong>
              </div>
              <div className='desk-card'>
                <span>Frais captés</span>
                <strong>{formatNum(tabStats.commission) || '--'} XAF</strong>
              </div>
            </div>
            <div className='tab-summary'>
              <div className='summary-card'>
                <span><FaFilter /> Lignes</span>
                <strong>{tabStats.count || '--'}</strong>
              </div>
              <div className='summary-card'>
                <span><FaChartLine /> Quantité</span>
                <strong>{formatNum(tabStats.quantity) || '--'}</strong>
              </div>
              <div className='summary-card'>
                <span><FaMoneyBillWave /> Montant</span>
                <strong>{formatNum(tabStats.amount) || '--'} XAF</strong>
              </div>
              <div className='summary-card'>
                <span><FaReceipt /> Commissions</span>
                <strong>{formatNum(tabStats.commission) || '--'} XAF</strong>
              </div>
            </div>
            <Tabs
              activeKey={tabIndex}
              type='line'
              onChange={(key: string) => setTabIndex(key)}
              size='small'
              items={tabItems}
            />
          </section>
        </div>
        <Drawer
          title='Détail opération'
          placement='right'
          width={560}
          open={!!selectedRecord}
          onClose={() => setSelectedRecord(undefined)}
          getContainer={false}
          bodyStyle={{ background: 'var(--mt-card)', padding: 16 }}
          headerStyle={{
            background: PRIMARY,
            borderBottom: '1px solid var(--mt-border)',
            color: YELLOW,
          }}
        >
          {selectedRecord && (
            <div className='detail-drawer'>
              <div className='detail-hero'>
                <span>{selectedKind}</span>
                <strong>{selectedRecord.code || selectedRecord.titre?.code || '--'}</strong>
                <div className='detail-total'>
                  <div>
                    <span>Quantité</span>
                    <strong>{formatNum(getRecordQuantity(selectedRecord)) || '--'}</strong>
                  </div>
                  <div>
                    <span>Prix</span>
                    <strong>{formatNum(Number(selectedRecord.prixNominale || 0)) || '--'} XAF</strong>
                  </div>
                  <div>
                    <span>Net estimé</span>
                    <strong>{formatNum(getRecordNetAmount(selectedRecord)) || '--'} XAF</strong>
                  </div>
                </div>
              </div>

              <section className='detail-section'>
                <h3>Cycle opérationnel</h3>
                <div className='timeline'>
                  {getLifecycleSteps(selectedRecord).map((step) => (
                    <div key={step.label} className={`timeline-step ${step.state}`}>
                      <i className='timeline-dot' />
                      <div>
                        <strong>{step.label}</strong>
                        <span>{step.description}</span>
                      </div>
                      <em className='timeline-status'>{step.status}</em>
                    </div>
                  ))}
                </div>
              </section>

              <section className='detail-section'>
                <h3>Matching desk</h3>
                <div className='matching-desk'>
                  <div className='leg-card'>
                    <span>Jambe source</span>
                    <strong>{formatOperationLabel(selectedRecord.typeOperation)}</strong>
                    <span>{formatNum(getRecordQuantity(selectedRecord)) || '--'} titres</span>
                    <strong>{formatNum(Number(selectedRecord.prixNominale || 0)) || '--'} XAF</strong>
                  </div>
                  <div className='match-arrow'>⇄</div>
                  <div className='leg-card'>
                    <span>Contrepartie attendue</span>
                    <strong>{formatOppositeOperation(selectedRecord.typeOperation)}</strong>
                    <span>À rapprocher selon quantité disponible</span>
                    <strong>{formatStatus(selectedRecord.statut)}</strong>
                  </div>
                </div>
              </section>

              <section className='detail-section'>
                <h3>Contrôle</h3>
                <div className='control-actions'>
                  <Button className='validate-action' onClick={() => setActionReview('validate')}>
                    Préparer validation
                  </Button>
                  <Button className='reject-action' onClick={() => setActionReview('reject')}>
                    Préparer rejet
                  </Button>
                  <Button className='settle-action' onClick={() => setActionReview('settle')}>
                    Suivre règlement
                  </Button>
                </div>
              </section>

              <section className='detail-section'>
                <h3>Instrument</h3>
                <div className='detail-grid'>
                  <DetailRow label='Code titre' value={selectedRecord.titre?.code} />
                  <DetailRow label='Émetteur' value={selectedRecord.titre?.pays} />
                  <DetailRow label='Instrument' value={selectedRecord.titre?.instrument} />
                  <DetailRow label='Opération' value={selectedRecord.typeOperation} />
                  <DetailRow
                    label='Statut'
                    value={
                      <StatusPill tone={getStatusTone(selectedRecord.statut)}>
                        {formatStatus(selectedRecord.statut)}
                      </StatusPill>
                    }
                  />
                  <DetailRow
                    label='Date opération'
                    value={formatDate(selectedRecord.dateOperation || selectedRecord.created_at)}
                  />
                </div>
              </section>

              <section className='detail-section'>
                <h3>Parties</h3>
                <div className='detail-grid'>
                  <DetailRow
                    label='Détenteur'
                    value={`${selectedRecord.detenteur?.code_referent || '--'} - ${selectedRecord.detenteur?.nom || '--'} ${selectedRecord.detenteur?.prenom || ''}`}
                    full
                  />
                  <DetailRow
                    label='Asset manager'
                    value={`${selectedRecord.assetManager?.code_referent || '--'} - ${selectedRecord.assetManager?.nom || '--'}`}
                  />
                  <DetailRow
                    label='Partenaire'
                    value={`${selectedRecord.partenaire?.code_referent || '--'} - ${selectedRecord.partenaire?.nom || '--'}`}
                  />
                  <DetailRow
                    label='Agence'
                    value={`${selectedRecord.agence?.code || '--'} - ${selectedRecord.agence?.nom || '--'}`}
                  />
                  <DetailRow
                    label='Gestionnaire'
                    value={`${selectedRecord.utilisateur?.nom || '--'} ${selectedRecord.utilisateur?.prenom || ''}`}
                  />
                </div>
              </section>

              <section className='detail-section'>
                <h3>Montants</h3>
                <div className='detail-grid'>
                  <DetailRow label='Quantité' value={formatNum(getRecordQuantity(selectedRecord))} />
                  <DetailRow label='Prix' value={`${formatNum(Number(selectedRecord.prixNominale || 0))} XAF`} />
                  <DetailRow label='Montant' value={`${formatNum(getRecordGrossAmount(selectedRecord))} XAF`} />
                  <DetailRow label='Commission' value={`${formatNum(getRecordCommission(selectedRecord))} XAF`} />
                  <DetailRow label='Coupon couru' value={`${formatNum(Number(selectedRecord.couponCouruPayer || 0))} XAF`} />
                  <DetailRow label='Net estimé' value={`${formatNum(getRecordNetAmount(selectedRecord))} XAF`} />
                  <DetailRow label='Décote' value={`${selectedRecord.decote || '--'} %`} />
                  <DetailRow label='Rendement' value={`${selectedRecord.tauxRendement || '--'} %`} />
                </div>
              </section>
            </div>
          )}
        </Drawer>
        <Modal
          open={!!actionReview}
          className='action-modal'
          title={getActionTitle(actionReview)}
          footer={[
            <Button key='close' className='reset-filter' onClick={() => setActionReview(null)}>
              Fermer
            </Button>,
          ]}
          onCancel={() => setActionReview(null)}
          getContainer={false}
        >
          <div className='action-review'>
            <p>
              Cette étape prépare le contrôle opérationnel. Aucun appel de validation
              backend n'est déclenché depuis ce reporting.
            </p>
            <div className='review-box'>
              <DetailRow label='Opération' value={selectedKind || '--'} />
              <DetailRow label='Titre' value={selectedRecord?.titre?.code || selectedRecord?.code} />
              <DetailRow label='Statut actuel' value={formatStatus(selectedRecord?.statut)} />
              <DetailRow label='Net estimé' value={`${formatNum(getRecordNetAmount(selectedRecord || {})) || '--'} XAF`} />
            </div>
          </div>
        </Modal>
      </Container>
    </Layout>
  );
};

const getRecordQuantity = (record: any) =>
  Number(record.qteRestant ?? record.qte ?? record.qteOriginal ?? 0);

const getRecordGrossAmount = (record: any) =>
  getRecordQuantity(record) * Number(record.prixNominale || 0);

const getRecordCommission = (record: any) =>
  Math.ceil(
    Number(record.commIntermediation || 0) +
      Number(record.commConservation || 0) +
      Number(record.commDroitGarde || 0) +
      Number(record.commBrokerage || 0),
  );

const getRecordNetAmount = (record: any) =>
  Math.ceil(
    getRecordGrossAmount(record) +
      Number(record.couponCouruPayer || 0) +
      getRecordCommission(record),
  );

const getLifecycleSteps = (record: any) => {
  const status = safeText(record?.statut).toLowerCase();
  const isMatched = status === 'match' || status === 'finalisé';
  const isFinalized = status === 'finalisé';

  return [
    {
      description: formatDate(record?.created_at || record?.dateOperation),
      label: 'Ordre reçu',
      state: 'done',
      status: 'Complété',
    },
    {
      description: isMatched
        ? 'Contrepartie identifiée et transaction constituée'
        : 'En attente de contrepartie compatible',
      label: 'Matching',
      state: isMatched ? 'done' : 'current',
      status: isMatched ? 'Match' : 'À traiter',
    },
    {
      description: isFinalized
        ? formatDate(record?.updated_at)
        : 'Validation back-office et contrôle des montants',
      label: 'Finalisation',
      state: isFinalized ? 'done' : isMatched ? 'current' : '',
      status: isFinalized ? 'Finalisé' : 'En attente',
    },
    {
      description: 'Règlement, livraison et rapprochement comptable',
      label: 'Règlement / Livraison',
      state: isFinalized ? 'current' : '',
      status: isFinalized ? 'À suivre' : 'Non démarré',
    },
  ];
};

const formatOperationLabel = (operation?: string) =>
  safeText(operation).toUpperCase() === 'VENTE' ? 'Vente' : 'Achat';

const formatOppositeOperation = (operation?: string) =>
  safeText(operation).toUpperCase() === 'VENTE' ? 'Achat attendu' : 'Vente attendue';

const getActionTitle = (action: 'validate' | 'reject' | 'settle' | null) => {
  if (action === 'validate') return 'Préparation validation';
  if (action === 'reject') return 'Préparation rejet';
  if (action === 'settle') return 'Suivi règlement / livraison';
  return 'Contrôle opérationnel';
};

const formatDate = (date?: string) =>
  date && !Number.isNaN(new Date(date).getTime())
    ? new Date(date).toLocaleDateString('fr-FR')
    : '--';

const getStatusTone = (status?: string) => {
  if (status === 'match' || status === 'finalisé') return 'success';
  return 'warning';
};

const formatStatus = (status?: string) => {
  if (status === 'match') return 'Match';
  if (status === 'finalisé') return 'Finalisé';
  return 'En cours';
};

const safeText = (value?: string | number | null) => `${value ?? ''}`;

const uniqueOptions = (values: Array<string | undefined>) =>
  Array.from(new Set(values.map((value) => safeText(value).trim()).filter(Boolean)));
